import React from "react";
import { useNavigate } from "react-router-dom";
import { Truck } from "lucide-react";
import { Card, ModernCardHeader } from "../../../components/cards";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { useOrders } from "../../orders/hooks";
import { DeliveryStatus } from "../../../__generated__/globalTypes";
import { formatDisplayDate } from "../../../helpers/date";
import { deliveryStatusLabel } from "../../../helpers/enum-labels";

export const OrdersToDeliverWidget: React.FC = () => {
  const navigate = useNavigate();
  const { data: toPlanData, loading: loadingToPlan } = useOrders({
    where: { deliveryStatus: DeliveryStatus.ToPlan },
  });
  const { data: plannedData, loading: loadingPlanned } = useOrders({
    where: { deliveryStatus: DeliveryStatus.Planned },
  });
  const { data: inProgressData, loading: loadingInProgress } = useOrders({
    where: { deliveryStatus: DeliveryStatus.InProgress },
  });

  const loading = loadingToPlan || loadingPlanned || loadingInProgress;

  const orders = [
    ...(toPlanData?.orders?.results ?? []),
    ...(plannedData?.orders?.results ?? []),
    ...(inProgressData?.orders?.results ?? []),
  ]
    .sort((a, b) => {
      if (!a.plannedDeliveryAt) return 1;
      if (!b.plannedDeliveryAt) return -1;
      return new Date(a.plannedDeliveryAt).getTime() - new Date(b.plannedDeliveryAt).getTime();
    })
    .slice(0, 6);

  return (
    <Card>
      <ModernCardHeader
        title="Commandes à livrer"
        subtitle="Livraisons à planifier ou en cours"
        action={
          <span
            className="text-xs text-indigo-600 cursor-pointer hover:underline"
            onClick={() => navigate("/orders?deliveryStatus=ToPlan")}
          >
            Voir tout
          </span>
        }
      />
      {loading ? (
        <Loading />
      ) : !orders.length ? (
        <EmptyList text="Aucune livraison en attente" />
      ) : (
        <div className="space-y-1 mt-3">
          {orders.map((order) => (
            <div
              key={`order-to-deliver-${order.id}`}
              className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200"
              onClick={() => navigate(`/order/${order.id}`)}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="bg-orange-50 text-orange-600 p-1.5 rounded-md flex-shrink-0">
                  <Truck className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">
                    {order.number ?? `#${order.id}`} — {order.customer?.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {order.plannedDeliveryAt
                      ? formatDisplayDate(order.plannedDeliveryAt)
                      : "Date non planifiée"}
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 whitespace-nowrap">
                {deliveryStatusLabel(order.deliveryStatus)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
